import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Application_Type,
  Criteria_Config_Insert_Input,
  Criteria_Group_Config,
  Mutation_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { CRITERIA_CONFIG_PROCESSES } from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import { insertCriteriaConfig } from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import {
  CriteriaConfigurationEvaluationCriteriaForm,
  EvaluationCriteriaFormValues,
} from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/edit/CriteriaConfigurationEvaluationCriteriaForm'
import {
  fetchCriteriaGroupsByProcesses,
  getSelectedApplicationTypeIdValues,
  getSelectedCriteriaGroupIdValue,
} from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/edit/EvaluationCriteriaFormUtils'
import { fetchApplicationTypesByProcesses } from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/EvaluationCriteriaGridUtils'
import { CreateButton } from 'src/shared/button/Buttons'
import { CRITERIA_TYPE } from 'src/shared/constants/assessment-constants'
import { APPLICATION_TYPE_FACTSHEET, CONFIGURATION_STATUS, PROCESS } from 'src/shared/constants/constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

const defaultInitialValues: EvaluationCriteriaFormValues = {
  criteriaType: CRITERIA_TYPE.QUALITY,
  criteriaId: '',
  status: CONFIGURATION_STATUS.ACTIVE,
  deDescription: '',
  enDescription: '',
  frDescription: '',
  itDescription: '',
  processAlloc: PROCESS.PF_KAP,
  pfKapCriteriaGroup: null,
  pfKapApplicationTypes: [],
  pfPgvCriteriaGroup: null,
  pfPgvApplicationTypes: [],
  factsheetCriteriaGroup: null,
  factsheetLevels: [],
}

const onCreate = () => {
  document.getElementById('evaluation-criteria-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const CriteriaConfigurationEvaluationCriteriaCreatePage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()

  const [initialValues, setInitialValues] = useState<EvaluationCriteriaFormValues | null>(null)
  const [criteriaGroups, setCriteriaGroups] = useState<Criteria_Group_Config[]>([])
  const [applicationTypes, setApplicationTypes] = useState<Application_Type[]>([])

  useEffect(() => {
    setInitialValues(defaultInitialValues)
  }, [])

  const factsheetApplicationTypes = useMemo(
    () => applicationTypes.filter((item) => item.process === PROCESS.FACTSHEET),
    [applicationTypes],
  )

  const fetchCriteriaGroups = useCallback(async () => {
    const { fetchedCriteriaGroups } = await fetchCriteriaGroupsByProcesses(urqlClient)(CRITERIA_CONFIG_PROCESSES)

    return fetchedCriteriaGroups
  }, [urqlClient])

  const fetchApplicationTypes = useCallback(async () => {
    const { fetchedApplicationTypes } = await fetchApplicationTypesByProcesses(urqlClient)(CRITERIA_CONFIG_PROCESSES)

    return fetchedApplicationTypes
  }, [urqlClient])

  const onBackHandler = useCallback(() => {
    navigate(ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationEvaluationCriteria.params({}))
  }, [navigate])

  const handleSubmitLocal = useCallback(
    async (values: EvaluationCriteriaFormValues, setFormDirty: Dispatch<boolean>) => {
      let applicationTypeIdsToInsert: number[]
      if (values.processAlloc === PROCESS.FACTSHEET) {
        // for FACTSHEET, always assign the ORIENTATION_LIST application type.
        applicationTypeIdsToInsert = factsheetApplicationTypes
          .filter((at) => at.code === APPLICATION_TYPE_FACTSHEET.ORIENTATION_LIST)
          .map((at) => at.id)
      } else {
        // for other processes, use the application types selected by the user.
        applicationTypeIdsToInsert = getSelectedApplicationTypeIdValues(values)
      }

      const criteriaConfigInsertInput: Criteria_Config_Insert_Input = {
        code: crypto.randomUUID(),
        criteria_type: values.criteriaType,
        criteria_id: values.criteriaId,
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        description: {
          de: values.deDescription ? HtmlSanitizer.sanitize(values.deDescription) : '',
          en: values.enDescription ? HtmlSanitizer.sanitize(values.enDescription) : '',
          fr: values.frDescription ? HtmlSanitizer.sanitize(values.frDescription) : '',
          it: values.itDescription ? HtmlSanitizer.sanitize(values.itDescription) : '',
        },
        processes: [values.processAlloc],
        criteria_group_id: getSelectedCriteriaGroupIdValue(values),
        application_type_ids: applicationTypeIdsToInsert,
        levels: values.factsheetLevels,
        sort_number: 0,
      }

      const { data, error } = await urqlClient
        .mutation<{
          insert_criteria_config: Mutation_Root['insert_criteria_config']
        }>(insertCriteriaConfig, {
          criteriaConfigInsertInput: [criteriaConfigInsertInput],
        })
        .toPromise()

      if (!error && data?.insert_criteria_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setFormDirty(false)
        onBackHandler()
      } else {
        notificationService.operationFailed()
      }
    },
    [factsheetApplicationTypes, notificationService, onBackHandler, urqlClient],
  )

  useEffect(() => {
    Promise.all([fetchCriteriaGroups(), fetchApplicationTypes()])
      .then(([criteriaGroups, applicationTypes]) => {
        setCriteriaGroups(criteriaGroups)
        setApplicationTypes(applicationTypes)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [fetchApplicationTypes, fetchCriteriaGroups, notificationService])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.evaluation.criteria.create')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={<CreateButton origin="header" onClick={onCreate} />}
    >
      <PageLayout>
        <>
          {initialValues && (
            <CriteriaConfigurationEvaluationCriteriaForm
              mode="CREATE"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              criteriaGroups={criteriaGroups}
              applicationTypes={applicationTypes}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
